import React, { useState, useEffect } from 'react';
import { Container, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

/**
 * Customer Experience Design
 * Sections: PURCHASE, ONBOARDING, ONGOING USE, SUCCESS METRICS, RETENTION TOUCHPOINTS
 */
function CCCustomerExperience() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [experience, setExperience] = useState({
    purchase: '',
    onboarding: '',
    ongoing_use: '',
    success_metrics: '',
    retention_touchpoints: ''
  });
  const [experienceId, setExperienceId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    if (selectedProductId !== null) {
      loadExperience(selectedProductId);
    }
  }, [selectedProductId]);

  const loadProducts = async () => {
    try {
      const response = await fetch('/api/products/get-catalog', { credentials: 'include' });
      const data = await response.json();
      if (data.catalog) {
        setProducts(data.catalog || []);
        if (data.catalog.length > 0) {
          setSelectedProductId(data.catalog[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProfitMapExperience = async () => {
    try {
      const response = await fetch('/api/business/field/customer_experience', { credentials: 'include' });
      const data = await response.json();
      if (data.field_value) {
        const parsed = JSON.parse(data.field_value);
        return {
          purchase: parsed.purchase || '',
          onboarding: parsed.onboarding || '',
          ongoing_use: parsed.ongoing_use || '',
          success_metrics: parsed.success_metrics || '',
          retention_touchpoints: parsed.retention_touchpoints || ''
        };
      }
    } catch (error) {
      console.error('Error loading Profit Map experience:', error);
    }
    return null;
  };

  const loadExperience = async (productId) => {
    try {
      const url = productId === 'general'
        ? '/api/email-sequences/experience'
        : `/api/email-sequences/context/${productId}`;

      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();

      if (data.success && data.customer_experience) {
        const exp = data.customer_experience;
        setExperience({
          purchase: exp.purchase || '',
          onboarding: exp.onboarding || '',
          ongoing_use: exp.ongoing_use || '',
          success_metrics: exp.success_metrics || '',
          retention_touchpoints: exp.retention_touchpoints || ''
        });
        setExperienceId(exp.id);
      } else {
        // Fall back to Profit Map customer experience data
        const profitMapExp = await loadProfitMapExperience();
        if (profitMapExp) {
          setExperience(profitMapExp);
        } else {
          setExperience({
            purchase: '',
            onboarding: '',
            ongoing_use: '',
            success_metrics: '',
            retention_touchpoints: ''
          });
        }
        setExperienceId(null);
      }
    } catch (error) {
      console.error('Error loading experience:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const payload = {
        ...experience,
        product_id: selectedProductId === 'general' ? null : selectedProductId
      };

      let response;
      if (experienceId) {
        response = await fetch(`/api/email-sequences/experience/${experienceId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch('/api/email-sequences/experience', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      }

      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Customer experience saved!' });
        if (data.experience?.id) {
          setExperienceId(data.experience.id);
        }
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to save' });
      }
    } catch (error) {
      console.error('Error saving:', error);
      setMessage({ type: 'error', text: 'Error saving experience' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setExperience(prev => ({ ...prev, [field]: value }));
  };

  const sections = [
    {
      key: 'purchase',
      title: 'PURCHASE',
      icon: '💳',
      description: 'What happens when someone buys? Checkout flow, confirmation, immediate access.',
      placeholder: 'Describe the purchase experience:\n- How do they checkout?\n- What confirmation do they receive?\n- How do they access what they bought?'
    },
    {
      key: 'onboarding',
      title: 'ONBOARDING',
      icon: '🚀',
      description: 'Getting started experience. First steps, welcome sequence, quick wins.',
      placeholder: 'Describe the onboarding flow:\n- What are the first steps?\n- How do you welcome them?\n- What quick win can they achieve?'
    },
    {
      key: 'ongoing_use',
      title: 'ONGOING USE',
      icon: '🔄',
      description: 'Regular engagement. How they use the product, support touchpoints.',
      placeholder: 'Describe ongoing engagement:\n- How do they use the product regularly?\n- What support is available?\n- How do you keep them engaged?'
    },
    {
      key: 'success_metrics',
      title: 'SUCCESS METRICS',
      icon: '📈',
      description: 'How you measure customer success. KPIs, milestones, outcomes.',
      placeholder: 'Define success metrics:\n- What indicates a customer is succeeding?\n- What milestones matter?\n- How do you track progress?'
    },
    {
      key: 'retention_touchpoints',
      title: 'RETENTION TOUCHPOINTS',
      icon: '🎯',
      description: 'Keeping them engaged. Check-ins, renewals, win-backs.',
      placeholder: 'Describe retention strategy:\n- How often do you check in?\n- What triggers a touchpoint?\n- How do you prevent churn?'
    }
  ];

  const sectionCardStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: `1px solid ${colors.greyLighter}`
  };

  const textareaStyle = {
    width: '100%',
    minHeight: '120px',
    padding: '1rem',
    fontSize: '0.95rem',
    border: `1px solid ${colors.greyLight}`,
    borderRadius: '8px',
    resize: 'vertical',
    lineHeight: '1.6',
    fontFamily: 'inherit'
  };

  if (loading) {
    return (
      <Container className="py-4">
        <p>Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4" style={{ maxWidth: '1000px' }}>
      <div className="mb-4">
        <Button
          variant="link"
          className="p-0 mb-2 text-decoration-none"
          style={{ color: colors.greyMid }}
          onClick={() => navigate('/command-center/market-intelligence')}
        >
          ← Back to Market Intelligence
        </Button>
        <h1>Customer Experience Design</h1>
        <p className="text-muted">
          Define the journey your customers take. This informs your email sequences and support touchpoints.
        </p>
      </div>

      {message && (
        <div
          className="mb-3 p-3 rounded"
          style={{
            backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
            color: message.type === 'success' ? '#155724' : '#721c24'
          }}
        >
          {message.text}
        </div>
      )}

      <div
        className="mb-4 p-3 rounded"
        style={{ backgroundColor: '#e8f4ff', border: '1px solid #b8daff' }}
      >
        <p className="mb-0" style={{ color: '#004085', fontSize: '0.9rem' }}>
          <strong>Tip:</strong> The customer experience you define here will be used by AI Assist to generate
          personalized email sequences. Be specific about what happens at each stage.
        </p>
      </div>

      <div
        className="mb-4 p-3 rounded"
        style={{ backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
      >
        <label style={{ fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>
          Select Product (or create a general experience for all products)
        </label>
        <select
          className="form-select"
          value={selectedProductId || ''}
          onChange={(e) => setSelectedProductId(e.target.value === 'general' ? 'general' : parseInt(e.target.value))}
        >
          <option value="general">General (applies to all products)</option>
          {products.map(product => (
            <option key={product.id} value={product.id}>
              {product.name} - ${product.price}
            </option>
          ))}
        </select>
      </div>

      {sections.map(section => (
        <div key={section.key} style={sectionCardStyle}>
          <div className="d-flex align-items-center gap-2 mb-2">
            <span style={{ fontSize: '1.5rem' }}>{section.icon}</span>
            <span style={{ fontSize: '1.1rem', fontWeight: '600', color: colors.greyDarkest, letterSpacing: '0.5px' }}>
              {section.title}
            </span>
          </div>
          <p className="text-muted small mb-3">{section.description}</p>
          <textarea
            style={textareaStyle}
            placeholder={section.placeholder}
            value={experience[section.key]}
            onChange={(e) => handleChange(section.key, e.target.value)}
          />
        </div>
      ))}

      <div className="d-flex justify-content-end mt-3">
        <Button
          variant="dark"
          style={{ backgroundColor: colors.greyDarkest }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Customer Experience'}
        </Button>
      </div>
    </Container>
  );
}

export default CCCustomerExperience;
